import React, { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, fileUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  ShieldAlert, Users, Flag, Film, LifeBuoy, Image as ImageIcon,
  Search, Trash2, Gem, Unlock, Star, Download, Play, Loader2, Wallet,
  Copy, CheckCircle2, ClipboardCheck,
} from "lucide-react";
import { ApprovalsPanel } from "@/components/admin/ApprovalsPanel";

const TABS = [
  { id: "stats", label: "Overview", Icon: ShieldAlert },
  { id: "approvals", label: "Approvals", Icon: ClipboardCheck },
  { id: "users", label: "Users", Icon: Users },
  { id: "safety", label: "Safety", Icon: Flag },
  { id: "stories", label: "Stories", Icon: Star },
  { id: "assets", label: "User Art/Music", Icon: ImageIcon },
  { id: "tickets", label: "Support", Icon: LifeBuoy },
  { id: "payouts", label: "Payouts", Icon: Wallet },
  { id: "promo", label: "Promo Videos", Icon: Film },
];

export default function Admin() {
  const { user, loading } = useAuth();
  const [tab, setTab] = useState("stats");

  if (loading) {
    return <div className="max-w-4xl mx-auto p-10 text-slate-500">Loading…</div>;
  }
  if (!user) return <Navigate to="/login?next=/admin" replace />;
  if (!user.is_admin) {
    return (
      <div className="max-w-xl mx-auto px-4 py-24 text-center">
        <ShieldAlert className="h-10 w-10 text-rose-400 mx-auto mb-4" />
        <h1 className="font-serif text-3xl font-bold mb-3">Admin access required</h1>
        <p className="text-slate-400 mb-6">This back office is for ProSeCraft admins only.</p>
        <Link to="/" className="text-amber-300 hover:text-amber-200 font-mono text-xs uppercase tracking-widest">← Back home</Link>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
      <div className="mb-8">
        <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-rose-400 mb-2">
          ProSeCraft · Admin
        </div>
        <h1 className="font-serif text-3xl md:text-4xl font-bold">Backstage</h1>
        <p className="text-slate-400 text-sm mt-1">
          Logged in as <span className="text-amber-300">{user.email}</span>
        </p>
      </div>

      <div className="flex flex-wrap gap-2 mb-6 border-b border-white/10 pb-3">
        {TABS.map(({ id, label, Icon }) => (
          <button
            key={id}
            data-testid={`admin-tab-${id}`}
            onClick={() => setTab(id)}
            className={`inline-flex items-center gap-2 h-9 px-3 rounded-full text-xs font-mono uppercase tracking-widest transition-colors ${
              tab === id
                ? "bg-amber-500 text-black"
                : "text-slate-400 hover:text-amber-300 border border-white/10"
            }`}
          >
            <Icon className="h-3.5 w-3.5" /> {label}
          </button>
        ))}
      </div>

      {tab === "stats"   && <StatsPanel />}
      {tab === "approvals" && <ApprovalsPanel />}
      {tab === "users"   && <UsersPanel />}
      {tab === "safety"  && <SafetyPanel />}
      {tab === "stories" && <StoriesPanel />}
      {tab === "assets"  && <AssetsPanel />}
      {tab === "tickets" && <TicketsPanel />}
      {tab === "payouts" && <PayoutsPanel />}
      {tab === "promo"   && <PromoPanel />}
    </div>
  );
}

// ---------- helpers ----------
const Card = ({ children, className = "" }) => (
  <div className={`rounded-2xl border border-white/10 bg-[#0F1117] p-5 ${className}`}>{children}</div>
);
const Field = ({ label, value, testid }) => (
  <div data-testid={testid}>
    <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500">{label}</div>
    <div className="font-serif text-3xl font-bold text-white">{value}</div>
  </div>
);

// ---------- panels ----------
const StatsPanel = () => {
  const [stats, setStats] = useState(null);
  useEffect(() => { api.get("/admin/stats").then(r => setStats(r.data)).catch(() => {}); }, []);
  if (!stats) return <div className="text-slate-500">Loading…</div>;
  return (
    <div data-testid="admin-stats" className="grid grid-cols-2 md:grid-cols-4 gap-4">
      <Card><Field testid="stat-users" label="Users" value={stats.users} /></Card>
      <Card><Field testid="stat-stories" label="Stories" value={stats.stories} /></Card>
      <Card><Field testid="stat-companions" label="Companions" value={stats.companions} /></Card>
      <Card><Field testid="stat-safety" label="Safety flags" value={stats.safety_log} /></Card>
      <Card><Field testid="stat-locked" label="Locked accounts" value={stats.locked_users} /></Card>
      <Card><Field testid="stat-tickets" label="Open tickets" value={stats.tickets_open} /></Card>
      <Card><Field testid="stat-promo" label="Promo videos" value={stats.promo_videos} /></Card>
    </div>
  );
};

const UsersPanel = () => {
  const [q, setQ] = useState("");
  const [users, setUsers] = useState([]);
  const [busy, setBusy] = useState(false);
  const load = () => {
    setBusy(true);
    api.get("/admin/users", { params: { q, limit: 50 } })
      .then(r => setUsers(r.data))
      .finally(() => setBusy(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  const unlock = async (uid) => {
    await api.post(`/admin/users/${uid}/unlock-steamy`);
    toast.success("Unlocked Steamy");
    load();
  };
  const unban = async (uid) => {
    await api.post(`/admin/users/${uid}/unban`);
    toast.success("Account restored (ban + steamy lock cleared, safety flags reset)");
    load();
  };
  const ban = async (uid) => {
    if (!window.confirm("Ban this account? They won't be able to log in.")) return;
    await api.post(`/admin/users/${uid}/ban`);
    toast.success("Account banned");
    load();
  };
  const grant = async (uid) => {
    const amt = Number(prompt("How many gems to grant?", "100"));
    if (!amt) return;
    await api.post(`/admin/users/${uid}/grant-gems`, { amount: amt });
    toast.success(`Granted ${amt} gems`);
    load();
  };
  const del = async (uid) => {
    if (!window.confirm("Delete this user permanently?")) return;
    await api.delete(`/admin/users/${uid}`);
    toast.success("Deleted");
    load();
  };
  return (
    <div>
      <div className="flex gap-2 mb-4">
        <div className="relative flex-1 max-w-md">
          <Search className="h-4 w-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input data-testid="admin-users-search" value={q} onChange={e => setQ(e.target.value)}
            placeholder="Search by email, name, or user_id"
            className="pl-9 bg-[#07080A] border-white/10 h-11" />
        </div>
        <Button data-testid="admin-users-search-btn" onClick={load} disabled={busy}>Search</Button>
      </div>
      <div className="overflow-x-auto rounded-2xl border border-white/10">
        <table data-testid="admin-users-table" className="w-full text-sm">
          <thead className="bg-white/5 text-left text-[10px] font-mono uppercase tracking-widest text-slate-500">
            <tr><th className="p-3">Email</th><th className="p-3">Gems</th><th className="p-3">Sub</th><th className="p-3">Status</th><th className="p-3">Actions</th></tr>
          </thead>
          <tbody>
            {users.map(u => (
              <tr key={u.user_id} className="border-t border-white/5">
                <td className="p-3">
                  <div className="text-slate-100">{u.email}</div>
                  <div className="text-[10px] font-mono text-slate-500">{u.user_id}</div>
                </td>
                <td className="p-3 text-amber-300">{u.credits}</td>
                <td className="p-3 text-slate-300">{u.subscription_status}</td>
                <td className="p-3">
                  {u.steamy_locked && <span className="rounded-full px-2 py-0.5 text-[10px] font-mono bg-rose-500/20 text-rose-200 border border-rose-500/40">LOCKED</span>}
                  {u.banned && <span className="ml-1 rounded-full px-2 py-0.5 text-[10px] font-mono bg-rose-700/40 text-rose-100 border border-rose-500/60">BANNED</span>}
                  {u.is_admin && <span className="ml-1 rounded-full px-2 py-0.5 text-[10px] font-mono bg-amber-500/20 text-amber-200 border border-amber-500/40">ADMIN</span>}
                </td>
                <td className="p-3 space-x-2 whitespace-nowrap">
                  <button data-testid={`unlock-${u.user_id}`} onClick={() => unlock(u.user_id)} className="inline-flex items-center gap-1 text-xs text-emerald-300 hover:text-emerald-200"><Unlock className="h-3 w-3" /> Unlock steamy</button>
                  <button data-testid={`unban-${u.user_id}`} onClick={() => unban(u.user_id)} className="inline-flex items-center gap-1 text-xs text-emerald-300 hover:text-emerald-200" title="Clear ban + steamy lock + safety flags"><Unlock className="h-3 w-3" /> Restore</button>
                  {!u.is_admin && !u.banned && (
                    <button data-testid={`ban-${u.user_id}`} onClick={() => ban(u.user_id)} className="inline-flex items-center gap-1 text-xs text-rose-300 hover:text-rose-200"><ShieldAlert className="h-3 w-3" /> Ban</button>
                  )}
                  <button data-testid={`grant-${u.user_id}`} onClick={() => grant(u.user_id)} className="inline-flex items-center gap-1 text-xs text-amber-300 hover:text-amber-200"><Gem className="h-3 w-3" /> Grant</button>
                  {!u.is_admin && (
                    <button data-testid={`delete-${u.user_id}`} onClick={() => del(u.user_id)} className="inline-flex items-center gap-1 text-xs text-rose-300 hover:text-rose-200"><Trash2 className="h-3 w-3" /> Delete</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const SafetyPanel = () => {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/admin/safety-log").then(r => setRows(r.data)); }, []);
  return (
    <div data-testid="admin-safety-log" className="space-y-2">
      {rows.length === 0 && <div className="text-slate-500 text-sm">No flagged attempts yet. Nice.</div>}
      {rows.map((r, i) => (
        <Card key={i}>
          <div className="flex items-center justify-between mb-1">
            <div className="font-mono text-[11px] uppercase tracking-widest text-rose-300">{r.category || "flag"} · {r.surface}</div>
            <div className="text-[10px] text-slate-500 font-mono">{new Date(r.created_at).toLocaleString()}</div>
          </div>
          <div className="text-xs text-slate-400">user_id: <span className="text-slate-300">{r.user_id}</span>{r.companion_id ? ` · companion: ${r.companion_id}` : ""}</div>
          <div className="mt-2 text-sm text-slate-200 italic">"{r.sample}"</div>
        </Card>
      ))}
    </div>
  );
};

const StoriesPanel = () => {
  const [rows, setRows] = useState([]);
  const load = () => api.get("/admin/stories").then(r => setRows(r.data));
  useEffect(() => { load(); }, []);
  const feature = async (sid, featured) => { await api.post(`/admin/stories/${sid}/feature`, { featured }); load(); };
  const del = async (sid) => { if (window.confirm("Delete this story?")) { await api.delete(`/admin/stories/${sid}`); load(); } };
  return (
    <div data-testid="admin-stories" className="grid gap-3">
      {rows.map(s => (
        <Card key={s.story_id} className="flex items-center justify-between gap-4">
          <div className="min-w-0 flex-1">
            <div className="font-serif text-lg text-white truncate">{s.title}</div>
            <div className="text-[11px] font-mono text-slate-500">by {s.author_name} · {s.primary_genre || s.genre} · votes {s.votes}</div>
          </div>
          <div className="flex items-center gap-2">
            <button data-testid={`feature-${s.story_id}`} onClick={() => feature(s.story_id, !s.is_featured)} className={`text-xs rounded-full h-8 px-3 border ${s.is_featured ? "bg-amber-500/20 border-amber-400 text-amber-200" : "border-white/10 text-slate-400 hover:text-amber-300"}`}>
              {s.is_featured ? "Featured ★" : "Feature"}
            </button>
            <button data-testid={`delete-story-${s.story_id}`} onClick={() => del(s.story_id)} className="text-rose-300 hover:text-rose-200 p-2"><Trash2 className="h-4 w-4" /></button>
          </div>
        </Card>
      ))}
    </div>
  );
};

const AssetsPanel = () => {
  const [rows, setRows] = useState([]);
  const load = () => api.get("/admin/user-assets").then(r => setRows(r.data));
  useEffect(() => { load(); }, []);
  const takedown = async (fid) => {
    if (!window.confirm("Remove this asset (user will be notified)?")) return;
    await api.post(`/admin/user-assets/${fid}/takedown`);
    toast.success("Removed");
    load();
  };
  return (
    <div data-testid="admin-assets" className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
      {rows.length === 0 && <div className="text-slate-500 text-sm col-span-full">No user-uploaded art or music yet.</div>}
      {rows.map(a => (
        <Card key={a.file_id}>
          {a.kind === "user_art" ? (
            <img src={fileUrl(a.url)} alt={a.title || a.file_id} className="w-full aspect-[4/3] object-cover rounded-lg border border-white/10" />
          ) : (
            <audio controls src={fileUrl(a.url)} className="w-full" />
          )}
          <div className="mt-2 text-xs text-slate-400">{a.title || "(untitled)"}</div>
          <div className="text-[10px] font-mono text-slate-500">{a.kind} · owner {a.owner_id}</div>
          <button data-testid={`takedown-${a.file_id}`} onClick={() => takedown(a.file_id)} className="mt-2 w-full text-xs rounded-full h-8 border border-rose-500/40 bg-rose-500/10 hover:bg-rose-500/20 text-rose-200">
            <Trash2 className="h-3 w-3 inline mr-1" /> Takedown (not their work)
          </button>
        </Card>
      ))}
    </div>
  );
};

const TicketsPanel = () => {
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("open");
  const load = () => {
    const params = filter === "all" ? {} : { resolved: filter === "resolved" };
    api.get("/admin/support-tickets", { params }).then(r => setRows(r.data));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);
  const resolve = async (tid, resolved) => {
    const note = resolved ? prompt("Resolution note (optional)") : "";
    await api.post(`/admin/support-tickets/${tid}/resolve`, { resolved, note });
    load();
  };
  return (
    <div data-testid="admin-tickets">
      <div className="flex gap-2 mb-3">
        {["open", "resolved", "all"].map(f => (
          <button key={f} data-testid={`ticket-filter-${f}`} onClick={() => setFilter(f)}
            className={`text-xs rounded-full h-8 px-3 font-mono uppercase tracking-widest border ${filter === f ? "bg-amber-500 border-amber-400 text-black" : "border-white/10 text-slate-400"}`}>
            {f}
          </button>
        ))}
      </div>
      <div className="space-y-3">
        {rows.length === 0 && <div className="text-slate-500 text-sm">No tickets.</div>}
        {rows.map(t => (
          <Card key={t.ticket_id}>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <div className="font-mono text-[11px] text-amber-300">[{t.category}] {t.name} · {t.email}</div>
                <div className="mt-1 text-slate-200 text-sm whitespace-pre-wrap">{t.message}</div>
                <div className="mt-1 text-[10px] text-slate-500 font-mono">{new Date(t.created_at).toLocaleString()}</div>
                {t.admin_note && <div className="mt-2 text-[11px] text-emerald-300">Resolution: {t.admin_note}</div>}
              </div>
              <button data-testid={`resolve-${t.ticket_id}`} onClick={() => resolve(t.ticket_id, !t.resolved)}
                className={`text-xs rounded-full h-8 px-3 border whitespace-nowrap ${t.resolved ? "bg-emerald-500/20 border-emerald-400 text-emerald-200" : "border-white/10 text-slate-400 hover:text-emerald-300"}`}>
                {t.resolved ? "Resolved ✓" : "Mark resolved"}
              </button>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
};

const PayoutsPanel = () => {
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("pending");
  const load = () => {
    const params = filter === "all" ? {} : { status: filter };
    api.get("/admin/payouts", { params }).then(r => setRows(r.data));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);
  const approve = async (pid) => {
    const ref = prompt("Reference (txn id, screenshot URL, etc.)") || "";
    await api.post(`/admin/payouts/${pid}/approve`, { reference: ref });
    toast.success("Marked as paid");
    load();
  };
  const reject = async (pid) => {
    const reason = prompt("Reason for rejection") || "Rejected";
    await api.post(`/admin/payouts/${pid}/reject`, { reject_reason: reason });
    toast.success("Rejected — gems refunded");
    load();
  };
  return (
    <div data-testid="admin-payouts">
      <div className="flex gap-2 mb-3">
        {["pending", "paid", "rejected", "all"].map(f => (
          <button key={f} data-testid={`payout-filter-${f}`} onClick={() => setFilter(f)}
            className={`text-xs rounded-full h-8 px-3 font-mono uppercase tracking-widest border ${filter === f ? "bg-amber-500 border-amber-400 text-black" : "border-white/10 text-slate-400"}`}>
            {f}
          </button>
        ))}
      </div>
      <div className="space-y-3">
        {rows.length === 0 && <div className="text-slate-500 text-sm">No payouts.</div>}
        {rows.map(p => (
          <Card key={p.payout_id}>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <div className="font-serif text-lg text-white">{p.gems} gems · ${p.usd_amount} <span className="text-xs font-mono text-slate-500">via {p.method}</span></div>
                <div className="text-xs text-slate-300 mt-1">To: <span className="text-amber-300 font-mono">{p.destination}</span></div>
                <div className="text-[11px] text-slate-500 font-mono mt-1">{p.author_name} · {p.author_email} · {new Date(p.created_at).toLocaleString()}</div>
                {p.note && <div className="mt-1 text-xs text-slate-400 italic">"{p.note}"</div>}
                {p.paid_reference && <div className="mt-1 text-[11px] text-emerald-300">Paid ref: {p.paid_reference}</div>}
                {p.reject_reason && <div className="mt-1 text-[11px] text-rose-300">Rejected: {p.reject_reason}</div>}
              </div>
              <div className="flex items-center gap-2">
                <span className={`rounded-full px-2 py-0.5 text-[10px] font-mono uppercase border whitespace-nowrap ${
                  p.status === "paid" ? "bg-emerald-500/20 text-emerald-200 border-emerald-400/40" :
                  p.status === "rejected" ? "bg-rose-500/20 text-rose-200 border-rose-400/40" :
                  "bg-amber-500/20 text-amber-200 border-amber-400/40"
                }`}>{p.status}</span>
                {p.status === "pending" && (
                  <>
                    <button data-testid={`payout-approve-${p.payout_id}`} onClick={() => approve(p.payout_id)}
                            className="text-xs rounded-full h-8 px-3 bg-emerald-500 hover:bg-emerald-400 text-black font-semibold">Mark paid</button>
                    <button data-testid={`payout-reject-${p.payout_id}`} onClick={() => reject(p.payout_id)}
                            className="text-xs rounded-full h-8 px-3 border border-rose-400/40 text-rose-200 hover:bg-rose-500/10">Reject</button>
                  </>
                )}
              </div>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
};

const PromoPanel = () => {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [orientation, setOrientation] = useState("vertical");
  const load = () => api.get("/admin/promo/videos").then(r => setRows(r.data));
  useEffect(() => {
    load();
    const t = setInterval(load, 7000);
    return () => clearInterval(t);
  }, []);
  const kick = async () => {
    setBusy(true);
    try {
      await api.post("/admin/promo/generate", { orientation });
      toast.success("Rendering started — this takes ~1-2 minutes");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };
  const falKick = async () => {
    try {
      await api.post("/admin/promo/fal-generate");
    } catch (e) {
      toast.info(e.response?.data?.detail || "Not configured");
    }
  };
  return (
    <div data-testid="admin-promo">
      <Card className="mb-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="font-serif text-xl text-white">Marketing promo video</div>
            <div className="text-xs text-slate-400">Generates a 2-3 minute MP4 narrating a Draco × Celene romance, uploaded to object storage.</div>
          </div>
          <div className="flex items-center gap-2">
            <select data-testid="promo-orientation" value={orientation} onChange={e => setOrientation(e.target.value)}
              className="h-9 bg-[#07080A] border border-white/10 rounded-full px-3 text-xs font-mono uppercase tracking-widest">
              <option value="vertical">Vertical 1080×1920</option>
              <option value="horizontal">Horizontal 1920×1080</option>
            </select>
            <Button data-testid="promo-generate" onClick={kick} disabled={busy} className="rounded-full bg-amber-500 hover:bg-amber-400 text-black">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Film className="h-4 w-4 mr-1" /> Generate</>}
            </Button>
            <Button data-testid="promo-fal" onClick={falKick} variant="outline" className="rounded-full border-white/15 text-slate-300">
              fal.ai (soon)
            </Button>
          </div>
        </div>
      </Card>
      <div className="space-y-3">
        {rows.map(v => (
          <Card key={v.video_id}>
            <div className="flex items-center justify-between gap-4">
              <div className="min-w-0 flex-1">
                <div className="font-mono text-[11px] text-amber-300">{v.video_id}</div>
                <div className="text-xs text-slate-400">
                  {v.status === "ready" ? `Ready · ${Math.round(v.duration_s)}s · ${(v.size/1e6).toFixed(1)}MB · ${v.width}×${v.height}` :
                   v.status === "failed" ? `Failed: ${v.error}` :
                   `${v.status} · ${v.progress || 0}%`}
                </div>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                {v.status === "ready" && (
                  <>
                    <a data-testid={`promo-preview-${v.video_id}`} href={fileUrl(v.download_url)} target="_blank" rel="noreferrer"
                       className="inline-flex items-center gap-1 text-xs text-amber-300 hover:text-amber-200">
                      <Play className="h-3 w-3" /> Preview
                    </a>
                    <a data-testid={`promo-download-${v.video_id}`} href={fileUrl(v.download_url)} download={`prosecraft-promo-${v.video_id}.mp4`}
                       className="inline-flex items-center gap-1 rounded-full bg-amber-500 hover:bg-amber-400 text-black text-xs font-semibold h-8 px-3">
                      <Download className="h-3 w-3" /> Download MP4
                    </a>
                    <button
                      data-testid={`promo-copy-share-${v.video_id}`}
                      onClick={async () => {
                        const shareUrl = `${window.location.origin}/promo/${v.video_id}`;
                        try { await navigator.clipboard.writeText(shareUrl); toast.success("Share URL copied"); }
                        catch { toast.info(shareUrl); }
                      }}
                      className="inline-flex items-center gap-1 rounded-full border border-white/10 text-xs text-slate-300 hover:text-amber-300 h-8 px-3"
                    >
                      <Copy className="h-3 w-3" /> Share URL
                    </button>
                    <button
                      data-testid={`promo-copy-tiktok-${v.video_id}`}
                      onClick={async () => {
                        const caption = (
                          "✨ New on ProSeCraft ✨\n\n" +
                          "A dragon-blood mafia king meets a swan who refuses to bow.\n" +
                          "Every reply is yours to write. Every chapter bends to you.\n\n" +
                          "19 AI companions · Steamy mode · Free to start · prosecraft.org\n\n" +
                          "#BookTok #DarkRomance #Beastkin #MafiaRomance #AIRomance #ProSeCraft #RomanceBooks #ReadersOfTikTok"
                        );
                        try { await navigator.clipboard.writeText(caption); toast.success("TikTok/Reels caption copied"); }
                        catch { toast.info("Copy failed — long-press to copy"); }
                      }}
                      className="inline-flex items-center gap-1 rounded-full border border-rose-400/40 text-xs text-rose-200 hover:bg-rose-500/10 h-8 px-3"
                    >
                      <CheckCircle2 className="h-3 w-3" /> Copy caption
                    </button>
                  </>
                )}
                {v.status !== "ready" && v.status !== "failed" && (
                  <Loader2 className="h-4 w-4 animate-spin text-slate-500" />
                )}
              </div>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
};
